import { QueryFactory } from '@lmt/shared-types';

import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CreateCommentBlock } from './CreateComment';
import { UserComment } from './UserComment';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import styles from './UserComments.module.scss';

export interface UserCommentsProps {
  patientId: number;
}

export const UserCommentsPanel: React.FC<UserCommentsProps> = (props) => {
  const { patientId } = props;
  const queryClient = useQueryClient();
  const claims = useClaims();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const i18n = useScopedTranslation('patients.overview.comments');

  const patientCommentQuery =
    QueryFactory.PatientCommentQuery.useGetQuery(patientId);
  const patientsQuery = QueryFactory.PatientQuery.useGetPatientsQuery();
  const comments = patientCommentQuery.data ?? [];

  const editComment = useCallback(async (id: number, comment: string) => {
    await QueryFactory.PatientCommentClient.edit(id, {
      comment: comment,
    });
    await invalidateComments();
  }, []);

  const createComment = useCallback(
    async (comment: string) => {
      await QueryFactory.PatientCommentClient.create({
        patientId: patientId,
        comment: comment,
      });
      await invalidateComments();
    },
    [patientId],
  );

  const deleteComment = useCallback(async (id: number) => {
    const isConfirmed = await modals.showDeletionConfirmation({
      title: i18n.t('delete_confirm_title'),
      text: i18n.t('delete_confirm_text'),
      okButtonText: i18n.t('delete_confirm_ok_text'),
    });
    if (isConfirmed) {
      await QueryFactory.PatientCommentClient.remove(id);
      await invalidateComments();
    }
  }, []);

  const invalidateComments = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.PatientCommentQuery.getQueryKey(patientId),
    });
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.PatientQuery.getPatientsQueryKey(),
    });
  }, [patientId]);

  return (
    <div className={styles.container} data-test-id={'user-comments'}>
      <div className={styles.header}>
        <div className={styles.title}>{i18n.t('title')}</div>
        <div className={styles.commentsCount} data-test-id={'comments-count'}>
          {comments.length}
        </div>
      </div>
      <div className={styles.commentsContainer}>
        {comments.map((comment, index) => {
          return (
            <div key={comment.id}>
              <UserComment
                comment={comment}
                isCurrentUser={claims.id === comment.userId}
                editComment={editComment}
                deleteComment={deleteComment}
                isLast={index === comments.length - 1}
              />
            </div>
          );
        })}
      </div>
      <div className={styles.newCommentContainer}>
        <CreateCommentBlock sendComment={createComment} />
      </div>
    </div>
  );
};
